import { ActionsObservable, combineEpics, Epic, ofType, StateObservable } from 'redux-observable';
import { delay, filter, map, mergeMap, tap, withLatestFrom } from 'rxjs/operators';
import { ItemIDs } from '../../Config/Items';
import { getMatchScore } from '../../Config/Tiles';
import BoardUtils from '../../Game/BoardUtils';
import Board from '../../Managers/Board/Board';
import FTUETracking, { FTUE_TRACK_STAGES } from '../../Managers/Tracking/FTUETracking';
import { FirecrackerBlacklist, MagnetBlacklist, Suits, TileSet } from '../../Tiles/TileSet';
import { TileSprite } from '../../UI/Game/Board/TileSprite';
import { checkGameComplete, checkGameStuck } from '../../Utils/GameComplete';
import { Actions, ActionTypes, TAction } from '../Actions/Actions';
import { BoosterId } from '../reducers/boosters';
import { getMatch, StuckReasons } from '../reducers/level';
import { IGameState } from '../Store/store';

// TODO get rid of side effects by moving to selectors
const startEpic: Epic<TAction, TAction, IGameState> = (
    action$,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.START_LEVEL),
        withLatestFrom(state$),
        // tap(([_, state]) => Board.compSetSprites(state.StateBoard.boardTiles)),
        map(() => Actions.setTilesprites())
        // set board tiles
    );
};

const boardReadyEpic: Epic<TAction, TAction, IGameState> = (
    action$,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.BOARD_READY),
        withLatestFrom(state$),
        map(([, state]) => {
            return Actions.activateFriendsBoosters(true);
        })
    );
};

/* const shuffleEpic: Epic<TAction, TAction, IGameState> = (
    action$,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.SHUFFLE_TILES),
        withLatestFrom(state$),
        withLatestFrom(state$),
        map(([_, state]) => {
            Board.scatterShuffle(state.StateBoard.boardMap).then(() =>
                Core.store.dispatch(Actions.removeBoosters([ItemIDs.SHUFFLE]))
            );
            // return Actions.setTilesprites();
        })
        // set board tiles
    );
}; */

//@ts-ignore
const selectEpic: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.SELECT_TILE),
        withLatestFrom(state$),
        mergeMap(([_, state]) => {
            const vals: string[] = state.StateLevel.selected.reduce((acc, s) => {
                if (!state.StateBoard.boardMap[s]) {
                    return acc;
                }
                const t = BoardUtils.formatId(state.StateBoard.boardMap[s].tileType);
                acc.push(t);
                return acc;
            }, []);
            const actions: any = [];

            vals.forEach((tileId, index) => {
                const { suit, value } = BoardUtils.convertTileData(tileId);
                if (suit === Suits.BOOSTER) {
                    const currentSelected = state.StateLevel.selected[index];
                    const boosterType = TileSet.getBoosterId(value);
                    actions.push(
                        Actions.storeMatches([currentSelected, currentSelected]),
                        Actions.addBoosters([boosterType])
                    );
                    return actions;
                }
            });

            if (vals.length < state.StateLevel.matchNum) {
                actions.push(Actions.matchTiles(state.StateLevel.selected));
                return actions;
            }

            const equals = getMatch(vals);

            if (!equals) {
                actions.push(Actions.matchTiles([]));

                return actions;
            }

            actions.push(Actions.matchTiles(state.StateLevel.selected));

            return actions;
        })
    );
};

//@ts-ignore
const matchEpic: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, any>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.MATCH_TILES),
        withLatestFrom(state$),
        filter(([_, state]) => state.StateLevel.matches.length > 0),
        tap(([_, state]) => {
            FTUETracking.track(FTUE_TRACK_STAGES.TILES_MATCHED, state);
            // Board.showBoardMatch(state.StateLevel.matches, state.StateBoard.boardMap);
        }),
        mergeMap(([_, state]) => {
            const vals = state.StateLevel.matches.map(m =>
                BoardUtils.formatId(state.StateBoard.boardMap[m].tileType)
            );
            const matches = vals.map(t => BoardUtils.convertTileData(t));
            const suit = matches[0].suit as Suits;
            const matchScore = getMatchScore(suit);

            const actions: any = [Actions.removeMatch([state.StateLevel.matches, vals])];
            const { timed } = state.StateLevel;
            if (!timed) {
                actions.push(Actions.addScore(matchScore * state.StateScore.multiplier));
            }

            if (suit === Suits.GOLD) {
                actions.push(Actions.updateCoins(1));
            }

            return actions;
        })
    );
};

const endMatchEpic: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction<string, string[][]>>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.REMOVE_MATCH),
        withLatestFrom(state$),
        delay(TileSprite.TILE_MATCH_TIME * 0.85),
        mergeMap(([action, state]) => {
            const matches = action.payload[1].map(t => BoardUtils.convertTileData(t));
            const suit = matches[0].suit as Suits;
            // const matchScore = getMatchScore(suit);
            // Board.showBurst(matchScore, state.StateScore.multiplier, suit === Suits.GOLD);
            const registerId = suit === Suits.GOLD ? Suits.GOLD : 'pair';

            const actions: TAction[] = [Actions.registerMove([registerId])];

            actions.push(Actions.setMultiplier(1));

            return actions;
        })
    );
};

const findEpic: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(ActionTypes.FIND_OBJECT),
        withLatestFrom(state$),
        delay(1400),
        withLatestFrom(state$),
        mergeMap(([_, state]) => {
            const removed = state.StateLevel.counted.map(() => 'toys');
            return [Actions.registerMove(removed), Actions.registerFound(state.StateLevel.counted)];
        })
    );
};

const availableEpic: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(
            ActionTypes.REMOVE_MATCH,
            ActionTypes.COUNT_OBJECTS,
            ActionTypes.SHUFFLE_TILES,
            ActionTypes.SET_TILESPRITES,
            ActionTypes.START_LEVEL,
            ActionTypes.REMOVE_GAME_BOOSTER,
            ActionTypes.REGISTER_MOVE
        ),
        withLatestFrom(state$),
        tap(([_, state]) => {
            // Board.showAvailable(state);
            // Board.showSelected(state.StateLevel.selected, state.StateBoard.boardMap);
        }),
        mergeMap(([_, state]) => {
            const na = state.StateBoard.available.filter(f => !state.StateLevel.found.includes(f));
            const nsp = na.filter(a => state.StateBoard.boardMap[a].tileType.suit === 'special');

            if (nsp.length > 0) {
                return [Actions.findObject(nsp), Actions.countObject(nsp)];
            }
            return [];
        })
    );
};

const boosterUsableEpic: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(
            ActionTypes.BOARD_READY,
            ActionTypes.REGISTER_MOVE,
            ActionTypes.SHUFFLE_TILES,
            ActionTypes.TUTORIAL_SET_BOOSTER,
            ActionTypes.ASK_FRIEND_START,
            ActionTypes.ASK_FRIEND_COMPLETE,
            ActionTypes.BOOSTER_ACTION_COMPLETE,
            ActionTypes.PAUSE_MENU_VISIBLE,
            ActionTypes.LEVEL_STUCK
        ),
        withLatestFrom(state$),
        mergeMap(([_, state]) => {
            const { tutorialBooster } = state.StateTutorial;

            const isTutorialBooster = (boosterId: BoosterId) => {
                return !tutorialBooster || tutorialBooster === boosterId;
            };
            const { helperFriend } = state.StateSocial;
            const { boardTiles, available, boardMap, availableMatches } = state.StateBoard;
            const reason = state.StateLevel.timed ? StuckReasons.TIME : StuckReasons.MATCHES;
            const paused = state.StatePause.inputPaused;
            const stuck = state.StateLevel.stuck && state.StateLevel.stuckReason !== reason;
            const hasMatches = availableMatches.length > 0;
            const highlightUsable =
                !paused &&
                !stuck &&
                hasMatches &&
                !helperFriend &&
                boardTiles.length > 0 &&
                isTutorialBooster(ItemIDs.HIGHLIGHT);

            const magnetTiles =
                !paused &&
                !stuck &&
                !helperFriend &&
                available.filter(
                    x => !MagnetBlacklist.includes(boardMap[x].tileType.suit as Suits)
                );
            const magnetUsable = magnetTiles.length > 0 && isTutorialBooster(ItemIDs.MAGNET);

            const firecrackerTiles = state.StateBoard.boardTiles.filter(
                x => !FirecrackerBlacklist.includes(x.tileType.suit as Suits)
            );
            const firecrackerUsable =
                !paused &&
                !stuck &&
                !helperFriend &&
                firecrackerTiles.length > 0 &&
                isTutorialBooster(ItemIDs.FIRECRACKER);

            const shuffleUsable =
                !paused && !stuck && !helperFriend && isTutorialBooster(ItemIDs.SHUFFLE);
            const timeUsable =
                !paused && !stuck && !helperFriend && isTutorialBooster(ItemIDs.TIME);

            Board.enableBoosters(ItemIDs.SHUFFLE, shuffleUsable);
            Board.enableBoosters(ItemIDs.TIME, timeUsable);
            Board.enableBoosters(ItemIDs.HIGHLIGHT, highlightUsable);
            Board.enableBoosters(ItemIDs.FIRECRACKER, firecrackerUsable);
            Board.enableBoosters(ItemIDs.MAGNET, magnetUsable);

            return [];
        })
    );
};

const winLoseEpic: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(
            ActionTypes.REGISTER_MOVE,
            ActionTypes.REGISTER_FOUND,
            ActionTypes.BOOSTER_ACTION_COMPLETE
        ),
        withLatestFrom(state$),
        filter(([_, state]) => !state.StateLevel.complete),
        mergeMap(([_, state]) => {
            if (
                state.StateLevel.found.length > 0 ||
                state.StateLevel.counted.length > 0 ||
                state.StateBoosters.currentBoosters.some(x => x.includes(ItemIDs.FIRECRACKER)) ||
                state.StateBoosters.currentBoosters.includes(ItemIDs.MAGNET)
            ) {
                return [];
            }
            const gameWinStatus = checkGameComplete(state);
            return gameWinStatus ? [Actions.completeLevel()] : [];
        })
    );
};

const stuckEpic: Epic<TAction, TAction, IGameState> = (
    action$: ActionsObservable<TAction>,
    state$: StateObservable<IGameState>
) => {
    return action$.pipe(
        ofType(
            ActionTypes.REGISTER_MOVE,
            ActionTypes.BOOSTER_ACTION_COMPLETE,
            ActionTypes.REGISTER_FOUND
        ),
        withLatestFrom(state$),
        mergeMap(([_, state]) => {
            if (
                state.StateLevel.found.length > 0 ||
                state.StateLevel.counted.length > 0 ||
                state.StateBoosters.currentBoosters.some(x => x.includes(ItemIDs.FIRECRACKER)) ||
                state.StateBoosters.currentBoosters.includes(ItemIDs.MAGNET)
            ) {
                return [];
            } else if (state.StateBoard.ready) {
                const stuck = checkGameStuck(state);
                return [Actions.setLevelStuck({ stuck, reason: StuckReasons.MATCHES })];
            }

            return [];
        })
    );
};

const boardEpic = combineEpics(
    startEpic,
    boardReadyEpic,
    // shuffleEpic,
    selectEpic,
    matchEpic,
    availableEpic,
    findEpic,
    endMatchEpic,
    winLoseEpic,
    stuckEpic,
    boosterUsableEpic
);

export default boardEpic;
